import { ChangeEvent, Dispatch, FunctionComponent, ReactNode } from 'react'
import { useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import BillingAddressInformation from '@/components/PaymentDetails/BillingAddressInformation'
import TipSelect from '@/components/TipSelect'
import { VenueBranding } from '@/libs/helpers/adapters'
import {
  CardCountry,
  GiftCard,
  GiftCardRequest,
  StreetAddressState,
  TBPaymentType,
} from '@/libs/helpers/apiClient'
import { OrderMethod } from '@/libs/helpers/utils'
import { OrderingAddress } from '@/providers/googleMaps'
import { Divider, Grid, Text } from '@/tbui'
import PayrixIframe from 'src/components/PayrixIframe'
import GiftCardPicker from './GiftCardPicker'
import SupportedCardBrands, { CardBrands } from './SupportedCardBrands'

interface OnlinePaymentProps {
  setTipAmount: Dispatch<string>
  cardCountry: CardCountry
  disableTip: boolean
  hasNoTipFF?: boolean
  handleDGCChange: (e: ChangeEvent<HTMLInputElement>) => void
  giftCardOptions: GiftCard[]
  isGiftCardPicked: boolean
  isDGCEnabled: boolean
  isECardsEnabled: boolean
  selectedGiftCardCode?: string | null
  applyGiftCardError: string | null
  handleAddGiftCard: ({ code, recaptchaToken, PIN }: GiftCardRequest) => void
  handleRemoveGiftCard: () => void
  isCartLoading: boolean
  isEmptyCart: boolean
  totalAfterTax: string
  tbPaymentType: TBPaymentType | null
  streetAddress: StreetAddressState | null
  setStreetAddress: (value: StreetAddressState | null) => void
  zipCode: string
  setZipCode: (value: string) => void
  validBillingAddress: boolean
  setValidZipCode: (value: boolean) => void
  setValidBillingAddress: (value: boolean) => void
  deliveryAddress?: OrderingAddress
  orderMethod: OrderMethod
  payrixSDKLoaded: boolean
  venueBranding?: VenueBranding
}

const OnlinePayment: FunctionComponent<OnlinePaymentProps> = ({
  setTipAmount,
  cardCountry,
  disableTip,
  hasNoTipFF = false,
  handleDGCChange,
  handleRemoveGiftCard,
  giftCardOptions,
  isGiftCardPicked,
  isDGCEnabled,
  isECardsEnabled,
  selectedGiftCardCode,
  applyGiftCardError,
  handleAddGiftCard,
  isCartLoading,
  isEmptyCart,
  totalAfterTax,
  tbPaymentType,
  streetAddress,
  setStreetAddress,
  zipCode,
  setZipCode,
  validBillingAddress,
  setValidZipCode,
  setValidBillingAddress,
  deliveryAddress,
  orderMethod,
  payrixSDKLoaded,
  venueBranding,
}) => {
  // INITIALIZE HOOKS
  const { t } = useTranslation()
  const theme = useTheme()

  const isDelivery = orderMethod === OrderMethod.delivery
  const shouldDisplayBillingAddress =
    tbPaymentType === TBPaymentType.PAYRIX || tbPaymentType === TBPaymentType.WORLDPAY
  const isPayrixPayment = tbPaymentType === TBPaymentType.PAYRIX

  if (isEmptyCart) {
    return null
  }

  const handleTipChange = (tipAmount: string): void => {
    setTipAmount(tipAmount)
  }

  const renderOnlinePaymentDetails = (): ReactNode => {
    return (
      <>
        {isPayrixPayment && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text type="H4">{t('checkout.payment.pay_gift_credit_debit')}</Text>
              <SupportedCardBrands
                theme={theme}
                brands={[CardBrands.VISA, CardBrands.MASTERCARD].concat(
                  cardCountry === CardCountry.CA ? [] : [CardBrands.AMEX, CardBrands.DISCOVER]
                )}
              />
            </div>
            <PayrixIframe payrixSDKLoaded={payrixSDKLoaded} venueBranding={venueBranding} />
          </>
        )}

        {shouldDisplayBillingAddress && (
          <BillingAddressInformation
            deliveryAddress={deliveryAddress}
            streetAddress={streetAddress}
            setStreetAddress={setStreetAddress}
            payrixZipCode={zipCode}
            setPayrixZipCode={setZipCode}
            validBillingAddress={validBillingAddress}
            setValidZipCode={setValidZipCode}
            setValidBillingAddress={setValidBillingAddress}
            orderMethod={orderMethod}
          />
        )}
      </>
    )
  }

  return (
    <>
      {!disableTip ? (
        <>
          <Text type="H4" m="24px 0">
            {t('checkout.payment.add_tip', {
              context: isDelivery,
            })}
          </Text>
          <TipSelect
            onChange={handleTipChange}
            totalAfterTax={totalAfterTax}
            isCartLoading={isCartLoading}
            hasNoTipFF={hasNoTipFF}
          />
        </>
      ) : null}
      {(isDGCEnabled || isECardsEnabled) && (
        <GiftCardPicker
          title={t('checkout.payment.pay_gift_card_online')}
          isCartLoading={isCartLoading}
          handleDGCChange={handleDGCChange}
          giftCardOptions={giftCardOptions}
          isGiftCardPicked={isGiftCardPicked}
          selectedGiftCardCode={selectedGiftCardCode}
          handleAddGiftCard={handleAddGiftCard}
          handleRemoveGiftCard={handleRemoveGiftCard}
          applyGiftCardError={applyGiftCardError}
          isECardsEnabled={isECardsEnabled}
        />
      )}
      <Grid m="20px 0">
        <Divider />
      </Grid>
      {renderOnlinePaymentDetails()}
    </>
  )
}

export default OnlinePayment
